import { GmrFunction, GmrFunctionParameter, GmrLovRow, GmrUnavailableStats, GmrUserInfo } from './gmr.models';

//
// Why this file exists.
//
// A Preset answer is plain JSON and JavaScript property access is case-sensitive, so a field whose
// name arrives in the wrong case does not fail - it reads as `undefined`, and the application carries
// on with a value it never got. That is not hypothetical: until 2026-08-14 four fields arrived as
// `USR_DEVELOPER_ACCESS_FLAG`, `ELASTIC_APM_ENVIRONMENT`, `ELASTIC_APM_SERVICE_NAME` and
// `GUI_IP_ADDRESS_EXTERNAL`, because Invantive UniversalSQL follows ANSI SQL and folds an unquoted
// alias to upper case while taking a "quoted" one verbatim - so in gmr-functions.sql the plain
// `me.<column> <alias>` items came back lower case and the ones computed from an expression did not.
// The visible symptom was a developer badge that never appeared; the invisible one was an
// `Invantive.Iwid` cookie written without its `oa` and `oi` keys for years, since `JSON.stringify`
// drops a property whose value is `undefined`. See ai/KNOWN_ISSUES.md.
//
// The check below turns that class of fault from silent into loud. It rests on two rules laid down by
// the maintainer on 2026-08-14:
//
//   1. **A Preset answer carries every field of its result set, including the ones that are null.**
//      Without this, "the key is absent" would be ambiguous - it could mean "no value" - and nothing
//      could be asserted. With it, an absent key can only mean renamed, dropped or miscased.
//   2. **Every field name is lower case, in every result set.** No exceptions: `unavailable_stats` and
//      `lov` were upper case until that date and were brought into line, so the convention no longer
//      has to be looked up per result set. In UniversalSQL that means quoting the alias -
//      `"available_cnt"` - since an unquoted identifier ANSI-folds to upper case.
//
// So there are two checks, and they answer different questions. The field lists say *this application
// cannot work without these names*, which catches a rename or a dropped column as well as a casing
// slip. The lower-case rule says *nothing may arrive in another case at all*, which also catches a
// field no code reads yet - the case that cost the most time here, because a new column can sit
// miscased in a template for weeks before anyone writes the code that needs it.
//
// A key caught by the first check is not reported again by the second: the message naming the field
// the application actually reads is the more useful of the two, and one fault should read as one
// fault.
//

//
// Strips the `[key: string]: unknown` index signature from a model interface. Without this,
// `keyof GmrUserInfo` is just `string` and the `satisfies` clauses below would accept any typo at
// all; with it, they are checked against the properties the model really declares, so renaming a
// field in gmr.models.ts breaks the build here rather than silently disarming the check.
//
type DeclaredKeys<T> = keyof
{
  [K in keyof T as string extends K ? never : number extends K ? never : K]: T[K]
};

//
// One entry per field read by name somewhere in src/app, and nothing beyond that. A field that is
// declared in gmr.models.ts but never read is left out on purpose: requiring it would turn a
// harmless backend tidy-up into an error modal for the customer. When code starts reading a new
// field, add it here in the same commit - that is the one manual step this mechanism cannot take
// over, and the reason each list is kept next to the interface it belongs to rather than inline in
// the service.
//

const FUNCTION_FIELDS =
[ 'cfn_code'
, 'cfn_preset_name'
, 'cfn_description'
, 'cfn_documentation'
, 'cfn_documentation_i18n'
, 'cfn_icon_url'
, 'cfn_form_method'
, 'cfn_form_encoding'
, 'cfn_action_button_label_i18n'
, 'cfrstat_cnt'
, 'can_description'
, 'can_description_i18n'
, 'can_custom_flag'
] as const satisfies readonly DeclaredKeys<GmrFunction>[];

//
// `cfr_default_value_arr` is absent on purpose: it is written by the client (selectFunction,
// selectAllDivisions) and never delivered by the backend, so requiring it would fail every run.
//
const FUNCTION_PARAMETER_FIELDS =
[ 'cfn_code'
, 'cfr_code'
, 'cfr_description'
, 'cfr_default_value'
, 'cfr_required_flag'
, 'cfr_user_hint'
, 'cfr_user_hint_i18n'
, 'cfr_lov_preset'
, 'cfr_depends_on'
, 'data_ind_html_control'
] as const satisfies readonly DeclaredKeys<GmrFunctionParameter>[];

const SYSTEM_USER_FIELDS =
[ 'usr_name'
, 'usr_company_name'
, 'usr_email_address'
, 'usr_language'
, 'data_container_id'
, 'daily_limit_min'
, 'daily_limit_max'
, 'daily_remaining_min'
, 'daily_remaining_max'
, 'low_scorers'
, 'usr_license_text_contents'
, 'usr_license_text_color'
, 'usr_license_text_hint'
, 'usr_developer_access_flag'
, 'd2d_user_identity'
, 'd2d_user_identity_previous'
, 'elastic_apm_service_name'
, 'gui_ip_address_external'
] as const satisfies readonly DeclaredKeys<GmrUserInfo>[];

//
// Upper case in the backend and in this application until 2026-08-14, when both were brought onto the
// one lower-case convention. These come from `sum()`/`listagg()` expressions, so their aliases have to
// be quoted in gmr-functions.sql to survive the ANSI folding.
//
const UNAVAILABLE_STATS_FIELDS =
[ 'outside_service_area_cnt'
, 'outside_service_area_list'
, 'compliance_cnt'
, 'compliance_list'
, 'inactive_cnt'
, 'inactive_list'
, 'unknown_cnt'
, 'unknown_list'
, 'available_cnt'
] as const satisfies readonly DeclaredKeys<GmrUnavailableStats>[];

//
// Renamed on the same date, in the eight `gmr-*lov-*.sql` templates as well as here. `code` is what
// gets posted as the report parameter, so this pair is the one place where a casing slip does not stop
// at a blank screen: it would fill a dropdown with entries that look selectable and carry nothing.
//
const LOV_FIELDS =
[ 'code'
, 'description'
] as const satisfies readonly DeclaredKeys<GmrLovRow>[];

export const PRESET_REQUIRED_FIELDS =
{
  functions: FUNCTION_FIELDS
, function_parameters: FUNCTION_PARAMETER_FIELDS
, system_users: SYSTEM_USER_FIELDS
, unavailable_stats: UNAVAILABLE_STATS_FIELDS
, lov: LOV_FIELDS
} as const;

export type PresetResultSetName = keyof typeof PRESET_REQUIRED_FIELDS;

//
// Checks one row against its result set's field list and returns a human-readable line per fault, or
// an empty array when the row honours the contract.
//
// A case-insensitive match is called out separately from a plain absence, because the two need
// different people: "delivered as X, expected x" is a one-word fix in the SQL template by whoever
// wrote the alias, while a field that is simply not there is a question about the query. Getting that
// distinction into the message is the whole point - the fault we are guarding against looks identical
// to "no data" from the outside, and only the delivered key name tells them apart.
//
// One row is enough for the array-shaped result sets: every row of a result set comes from the same
// select list, so a column name cannot differ between them.
//
export function findContractViolations(resultSetName: PresetResultSetName, row: unknown): string[]
{
  //
  // No row is not a contract fault. A caller may legitimately hold no reports, no parameters or an
  // empty list of values, and the code that needs a row already has to cope with its absence -
  // reporting on it here would put an error modal in front of an ordinary empty catalogue.
  //
  if (row == null)
  {
    return [];
  }

  if (typeof row !== 'object')
  {
    return [`Result set '${resultSetName}' carries ${typeof row} where a row object is expected.`];
  }

  const delivered = Object.keys(row as Record<string, unknown>);

  //
  // Built once per call rather than per field: a result set with 40 columns checked against 18
  // required names would otherwise walk the delivered list 18 times to answer a question that only
  // arises when something is already wrong.
  //
  const deliveredByLowerCase = new Map<string, string>();
  for (const key of delivered)
  {
    deliveredByLowerCase.set(key.toLowerCase(), key);
  }

  const violations: string[] = [];

  //
  // Names this application cannot work without. Reported first, and the delivered keys they account
  // for are remembered so the lower-case sweep below does not say the same thing a second time in
  // weaker words.
  //
  const alreadyReported = new Set<string>();

  for (const field of PRESET_REQUIRED_FIELDS[resultSetName] as readonly string[])
  {
    if (Object.prototype.hasOwnProperty.call(row, field))
    {
      continue;
    }

    const caseVariant = deliveredByLowerCase.get(field.toLowerCase());

    if (caseVariant != null)
    {
      alreadyReported.add(caseVariant);
    }

    violations.push(caseVariant != null
      ? `Result set '${resultSetName}' delivers '${caseVariant}' where '${field}' is expected; field names are case-sensitive.`
      : `Result set '${resultSetName}' does not deliver '${field}'.`
    );
  }

  //
  // Rule 2: every delivered name is lower case, whether or not anything reads it. This is the check
  // that fires on the day a column is added, rather than weeks later when someone writes the code
  // that needs it and finds `undefined`. Compared against the locale-independent `toLowerCase()` the
  // reading code would itself use; a name with no letters in it, `total_cnt` say, trivially passes.
  //
  for (const key of delivered)
  {
    if (key === key.toLowerCase() || alreadyReported.has(key))
    {
      continue;
    }

    violations.push(`Result set '${resultSetName}' delivers '${key}'; every field name must be lower case.`);
  }

  return violations;
}
